/**
 * Период раздела «Статистика»: один на все вкладки и подэкраны.
 *
 * Источник — адрес (`range`, `year`/`month`, `from`/`to`), чтобы ссылкой
 * можно было поделиться. Если в адресе периода нет — последний выбранный
 * в этом браузере, и только потом «30 дней». Переключение периода другие
 * параметры адреса (аккаунт, отчёт, фильтры) не трогает.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { readRemembered, writeRemembered } from '../../../shared/lib/remembered';
import type { DatePresetId, DateRangeOption } from '../types/account-stats';

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fromLocalDateString(str: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

const PRESET_IDS: DatePresetId[] = ['lastWeek', 'last7', 'last30', 'last90', 'last365', 'all'];
export const DEFAULT_DATE_RANGE: DateRangeOption = { type: 'preset', id: 'last30' };

/** Параметры адреса, в которых живёт период. */
export const PERIOD_PARAM_KEYS = ['range', 'year', 'month', 'from', 'to'] as const;

const REMEMBERED_KEY = 'stats:period';

/** Период из адреса; `null` — в адресе его нет (или он битый). */
export function parseDateRangeParams(params: URLSearchParams): DateRangeOption | null {
  const range = params.get('range');
  const from = params.get('from');
  const to = params.get('to');

  if (!range) {
    // Голые from/to — так период передают ссылкой из других экранов.
    const f = from ? fromLocalDateString(from) : null;
    const t = to ? fromLocalDateString(to) : null;
    return f && t ? { type: 'custom', from: f, to: t } : null;
  }

  if (PRESET_IDS.includes(range as DatePresetId)) {
    return { type: 'preset', id: range as DatePresetId };
  }

  if (range === 'month') {
    const year = Number(params.get('year'));
    const month = Number(params.get('month'));
    if (year && month >= 1 && month <= 12) return { type: 'month', year, month };
  }

  if (range === 'custom') {
    const f = from ? fromLocalDateString(from) : null;
    const t = to ? fromLocalDateString(to) : null;
    if (f && t) return { type: 'custom', from: f, to: t };
  }

  return null;
}

/** Пишет период в `target`, стирая прежний; прочие параметры остаются. */
export function writeDateRangeParams(target: URLSearchParams, option: DateRangeOption): URLSearchParams {
  for (const key of PERIOD_PARAM_KEYS) target.delete(key);

  if (option.type === 'preset') {
    target.set('range', option.id);
  } else if (option.type === 'month') {
    target.set('range', 'month');
    target.set('year', String(option.year));
    target.set('month', String(option.month));
  } else {
    target.set('range', 'custom');
    target.set('from', toLocalDateString(option.from));
    target.set('to', toLocalDateString(option.to));
  }
  return target;
}

export function dateRangeToSearchString(option: DateRangeOption): string {
  return writeDateRangeParams(new URLSearchParams(), option).toString();
}

/** Последний выбранный в этом браузере период (строкой адреса — Date в JSON не живёт). */
export function readRememberedDateRange(): DateRangeOption | null {
  const saved = readRemembered<string | null>(REMEMBERED_KEY, null);
  return saved ? parseDateRangeParams(new URLSearchParams(saved)) : null;
}

export function rememberDateRange(option: DateRangeOption): void {
  writeRemembered(REMEMBERED_KEY, dateRangeToSearchString(option));
}

/** Адрес главнее памяти, память главнее значения по умолчанию. */
export function pickDateRange(
  fromUrl: DateRangeOption | null,
  remembered: DateRangeOption | null,
): DateRangeOption {
  return fromUrl ?? remembered ?? DEFAULT_DATE_RANGE;
}

export function useDateRangeParams(): [DateRangeOption, (option: DateRangeOption) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const fromUrl = parseDateRangeParams(searchParams);
  const picked = pickDateRange(fromUrl, fromUrl ? null : readRememberedDateRange());
  // Разбор адреса даёт новый объект на каждой отрисовке. Экраны кладут период
  // в зависимости useMemo/useEffect, и новый объект перезапускал их по кругу:
  // «Сводка» с TikTok-аккаунтами вешала вкладку (04.11.2026). Тот же период —
  // тот же объект.
  const pickedKey = dateRangeToSearchString(picked);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateRangeOption = useMemo(() => picked, [pickedKey]);

  // Пришли по ссылке с периодом — он и становится «последним выбранным».
  const urlKey = fromUrl ? dateRangeToSearchString(fromUrl) : '';
  useEffect(() => {
    if (urlKey) writeRemembered(REMEMBERED_KEY, urlKey);
  }, [urlKey]);

  const setDateRangeOption = useCallback(
    (option: DateRangeOption) => {
      rememberDateRange(option);
      setSearchParams((prev) => writeDateRangeParams(new URLSearchParams(prev), option), { replace: true });
    },
    [setSearchParams],
  );

  return [dateRangeOption, setDateRangeOption];
}
